"use client";

import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

type SplashPhase = "idle" | "pulse" | "waiting" | "exit" | "done";

// Shared signal so children (e.g. AuthContext) can tell the splash the app is ready,
// even though the splash sits outside their provider tree.
let resolveReady: () => void;
let readyPromise = new Promise<void>((r) => {
  resolveReady = r;
});

export function signalAppReady() {
  resolveReady();
}

export default function NativeSplashOverlay() {
  // Start false so server and client agree (no hydration mismatch).
  // On native the Capacitor splash (launchAutoHide:false) covers the screen
  // until useEffect fires, so there is no visible gap.
  const [isNative, setIsNative] = useState(false);
  const [splashPhase, setSplashPhase] = useState<SplashPhase>("done");
  const [appReady, setAppReady] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    setIsNative(true);
    setSplashPhase("idle");

    // Web overlay is identical to native splash (same white bg, same logo).
    // Hide native splash — swap is invisible.
    SplashScreen.hide();

    // Immediately start pulsing
    requestAnimationFrame(() => setSplashPhase("pulse"));

    // Listen for app readiness (auth resolved)
    readyPromise.then(() => setAppReady(true));
  }, []);

  useEffect(() => {
    if (!isNative) return;
    let t: ReturnType<typeof setTimeout>;
    if (splashPhase === "pulse") {
      // After pulse animation, check if app is ready
      t = setTimeout(() => {
        setSplashPhase(appReady ? "exit" : "waiting");
      }, 1400);
    } else if (splashPhase === "waiting" && appReady) {
      // App became ready while waiting — exit immediately
      setSplashPhase("exit");
    } else if (splashPhase === "exit") {
      t = setTimeout(() => setSplashPhase("done"), 500);
    }
    return () => clearTimeout(t!);
  }, [splashPhase, isNative, appReady]);

  if (!isNative || splashPhase === "done") return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
      style={{
        animation:
          splashPhase === "exit"
            ? "splash-fade-out 500ms ease-in forwards"
            : undefined
      }}
    >
      {/* Glow ring */}
      <div
        className="absolute rounded-full"
        style={{
          width: 160,
          height: 160,
          background:
            "radial-gradient(circle, rgba(219,154,31,0.2) 0%, rgba(219,154,31,0) 70%)",
          opacity: 0,
          animation:
            splashPhase === "pulse"
              ? "splash-glow 1400ms ease-in-out forwards"
              : undefined
        }}
      />

      {/* Logo — already visible, matching native splash, starts pulsing */}
      <img
        src="/wiswizlogo-t.png"
        alt=""
        width={140}
        height={140}
        style={{
          animation:
            splashPhase === "pulse"
              ? "splash-logo-breathe 1400ms ease-in-out"
              : splashPhase === "exit"
                ? "splash-logo-exit 500ms ease-in forwards"
                : undefined
        }}
      />
    </div>
  );
}
